import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { meetingsRequest } from '../../../api/projectManagement';
import type { SalesRepSlug } from '../../ProjectManagement/team';

// Mirrors packages/api/src/modules/sales-bookings/list.ts.

export type DemoBookingSource =
  | 'website_demo_request'
  | 'sdr_personal_form'
  | 'sdr_confirmed'
  | 'rep_personal_link'
  | 'patrick_calendly'
  | 'round_robin_embed'
  | 'shared_link'
  | 'marketing_link'
  | 'marketing_influenced'
  | 'partner'
  | 'unclassified';

export type MeetingKind = 'demo' | 'discovery' | 'pricing' | 'trial' | 'partner' | 'other';

export interface DemoBooking {
  id: string;
  /** When the person first hit "Schedule" (the original booking, for a rescheduled one). */
  bookedAt: string;
  meetingStartAt: string;
  status: 'active' | 'canceled';
  /** Moved at least once; this is the live new booking. */
  rescheduled: boolean;
  source: DemoBookingSource;
  sourceLabel: string;
  sourceDetail: string | null;
  meetingKind: MeetingKind;
  /** A demo event type — the only kind the headline counts. */
  isDemo: boolean;
  /** Id of the earlier demo by the same person (within 30 days) this one repeats; null when it counts. */
  repeatOf: string | null;
  eventName: string | null;
  eventTypeName: string | null;
  schedulingUrl: string | null;
  hostRepSlug: SalesRepSlug | null;
  hostEmails: string[];
  inviteeName: string | null;
  inviteeEmail: string;
  /** The invitee's email domain as Calendly gave it (e.g. "giz.de"). */
  inviteeCompany: string | null;
  /** The Attio company behind the invitee's email domain; null when Attio has none. Older APIs omit it. */
  attioCompanyName?: string | null;
  attioCompanyRecordId?: string | null;
}

/** Mirrors SdrMeetingsBridge in packages/api/src/modules/project-management/sdr-form-bookings.ts. */
export interface SdrMeetingsBridge {
  definition: string;
  /** SDR meetings in the window, from Fillout: the one definition. */
  count: number;
  /** Counted Calendly demos tagged "SDR personal form" in the window. */
  calendlyCount: number;
  matched: number;
  calendlyMatched: number;
  onOtherSource: Array<{ name: string | null; email: string | null; day: string; bookingEmail: string; sourceLabel: string }>;
  withoutBooking: Array<{ name: string | null; email: string | null; company: string | null; day: string }>;
  calendlyOnly: Array<{ name: string | null; email: string; bookedAt: string }>;
}

export interface DemoBookingsResponse {
  generatedAt: string;
  rangeFrom: string;
  rangeTo: string;
  /** Every booking made on or after this date is in the table. */
  completeFrom: string | null;
  rangeComplete: boolean;
  sync: {
    /** Newest fully successful sync run. */
    lastSuccessAt: string | null;
    /** The newest finished run failed or partly failed. */
    problem: { at: string; kind: 'failed' | 'partial'; error: string } | null;
  };
  /** Demo bookings in the window, repeats included and marked. */
  bookings: DemoBooking[];
  /** Every other meeting booked in the window. */
  otherMeetings: DemoBooking[];
  demos: {
    bookings: number;
    /** Counted once per person per `repeatWindowDays` — the headline. */
    distinct: number;
    repeats: number;
    upcoming: number;
    cancelled: number;
    repeatWindowDays: number;
  };
  /** Counted demos by source. */
  countsBySource: Array<{ source: DemoBookingSource; label: string; count: number }>;
  otherMeetingsByKind: Array<{ kind: MeetingKind; count: number }>;
  excluded: { tests: number; rescheduledCopies: number; rescheduleTargetMissing: number };
  /** SDR meetings from Fillout with the bridge to the Calendly tag. Older APIs omit it. */
  sdrMeetings?: SdrMeetingsBridge;
}

export async function getDemoBookings(params: { from: string; to: string }): Promise<DemoBookingsResponse> {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  return meetingsRequest('Demo bookings', (signal) => apiClient.get(`/project-management/bookings/demo?${qs.toString()}`, { signal }));
}

/** Demo bookings and other meetings booked in the window, newest booking first. */
export function useDemoBookings(range: { from: string; to: string }) {
  return useQuery({
    queryKey: ['sales', 'bookings', 'demo', range.from, range.to],
    queryFn: () => getDemoBookings(range),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    // A timeout is already a 25 s wait; retrying it doubles the skeleton time.
    retry: false,
  });
}
